import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_aa4f8546-5d46-42d2-8104-f84919deefe1/artifacts/prtdjqf5_NWP%20white.png";

export default function Login() {
  const { t } = useTranslation();
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [shakeKey, setShakeKey] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    api
      .get("/auth/me")
      .then((res) => {
        if (mounted && res.data?.authenticated) navigate("/calculator", { replace: true });
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setError("");
    setSubmitting(true);
    try {
      await api.post("/auth/login", { password });
      // Post-login sanity check — verifies the session cookie actually landed
      // (Safari ITP / Vivaldi strict tracking protection can silently drop cookies).
      const meResp = await api.get("/auth/me");
      if (!meResp?.data?.authenticated) {
        // Synthesize a distinguishable error the catch block can localize.
        // eslint-disable-next-line no-throw-literal
        throw { response: { status: 0, data: { detail: "cookie_blocked" } } };
      }
      navigate("/calculator", { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      const serverDetail = err?.response?.data?.detail;
      let msg;
      if (serverDetail === "cookie_blocked") {
        msg = t("login.error_cookie_blocked");
      } else if (status === 401) {
        msg = t("login.error_wrong_password");
      } else if (status === 429) {
        msg = t("login.error_rate_limited");
      } else if (status && status >= 500) {
        msg = t("login.error_generic");
      } else {
        // network error / CORS / timeout — err.response is undefined
        msg = t("login.error_generic");
      }
      // eslint-disable-next-line no-console
      console.error("[login] failed", { status, code: err?.code, detail: serverDetail });
      setError(msg);
      setShakeKey((k) => k + 1);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="login-page"
      className="min-h-screen flex flex-col items-center justify-center px-4 py-12 relative"
    >
      {/* Language switcher — fixed top-right */}
      <div className="absolute top-4 right-4 sm:top-6 sm:right-6 z-20">
        <LanguageSwitcher />
      </div>

      <div
        key={shakeKey}
        data-testid="login-card"
        className={`nwp-card nwp-fade-up w-full max-w-md p-8 sm:p-10 ${error ? "nwp-shake" : ""}`}
        style={{
          background: "linear-gradient(180deg, rgba(22,22,22,0.92) 0%, rgba(14,14,14,0.92) 100%)",
          backdropFilter: "blur(6px)",
        }}
      >
        <div className="flex flex-col items-center mb-8">
          <img
            src={LOGO_URL}
            alt="NWP"
            data-testid="nwp-logo"
            className="h-24 w-auto mb-4"
            style={{ objectFit: "contain" }}
          />
          <div className="nwp-eyebrow mb-2" style={{ color: "var(--nwp-gold)" }}>
            {t("login.eyebrow")}
          </div>
          <p className="text-sm text-center" style={{ color: "var(--nwp-text-muted)" }}>
            {t("login.tagline")}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
          <div>
            <label
              htmlFor="password"
              className="text-xs mb-2 block tracking-wider uppercase"
              style={{ color: "var(--nwp-text-dim)", letterSpacing: "0.12em" }}
            >
              {t("login.password_label")}
            </label>
            <Input
              id="password"
              type="password"
              data-testid="password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              className="nwp-input h-11 text-base"
              placeholder="••••••••••"
            />
            {error && (
              <div
                data-testid="login-error"
                className="text-xs mt-2"
                style={{ color: "#ff5a55" }}
              >
                {error}
              </div>
            )}
          </div>

          <button
            type="submit"
            data-testid="login-submit-button"
            disabled={submitting || !password}
            className="nwp-btn-primary w-full rounded-xl font-medium"
            style={{ height: 48, fontSize: 15 }}
          >
            {submitting ? t("login.submitting") : t("login.submit")}
          </button>

          <div
            className="flex items-center justify-center gap-2 text-[11px] pt-1"
            style={{ color: "var(--nwp-text-muted)" }}
          >
            <ShieldCheck size={13} />
            <span>{t("login.secure_hint")}</span>
          </div>
        </form>
      </div>

      <footer className="mt-10 text-center" data-testid="login-footer">
        <div className="text-xs" style={{ color: "#6b6b6b" }}>
          {t("login.footer_copyright")}
          <span className="mx-2" style={{ color: "rgba(255,255,255,0.18)" }}>·</span>
          <Link
            to="/admin/login"
            data-testid="admin-link"
            className="text-xs transition-colors no-underline hover:underline"
            style={{ color: "rgba(255,255,255,0.30)" }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.60)")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.30)")}
          >
            {t("login.footer_admin")}
          </Link>
        </div>
        <div className="text-[10px] mt-1" style={{ color: "#4a4a4a", letterSpacing: "0.12em" }}>
          {t("login.footer_brand")}
        </div>
      </footer>
    </div>
  );
}
